import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useCallback } from "react";

import { type CancellationAlertAction } from "../components/CancellationAlertModal";

/**
 * Maps canonical words to their alternate spellings/synonyms.
 *
 * Order matters for multi-word patterns that share words with single-word patterns.
 * For example, "would not" must be processed before "would" because both patterns
 * can match in "i would not go" - if "would" → "can" runs first, "would not" → "wouldnt"
 * would never match. Longer patterns are listed first to ensure correct processing.
 *
 * Note: Single-word patterns like "2day" vs "2" don't have this issue because word
 * boundary matching (\b) makes them mutually exclusive - "2" won't match inside "2day".
 */
const WORD_NORMALIZATIONS: Array<[string, string[]]> = [
  // === COMMON TYPOS (must come before their component single-word patterns) ===
  ["wanna", ["wana"]],
  ["coming", ["comming", "comng"]],
  ["able", ["abel"]],
  ["unable", ["unabel"]],
  ["make", ["mke", "mae"]],
  ["feeling", ["feling"]],
  ["sick", ["sik", "sicck"]],
  ["broke", ["brok"]],
  ["emergency", ["emergnecy", "emergny"]],
  ["family", ["famly"]],
  ["remove", ["remov"]],

  // Text speak, slang, and dialects
  ["today", ["2day", "todays"]],
  ["tomorrow", ["tmrw", "tomorrows"]],
  ["you", ["u", "yall"]],
  ["please", ["pls", "plz"]],
  ["from", ["frm"]],
  ["sorry", ["sry"]],
  ["to", ["2", "too"]],

  // Cancel typos (NOT remove/drop - those have other meanings like "drop off")
  ["cancel", ["cancle", "canel", "cansel", "cacnel", "canncel", "canecl", "cancell"]],
  ["canceled", ["cancelled"]],
  ["canceling", ["cancelling"]],

  // Shift/schedule typos
  ["shift", ["shfit", "sift", "shft", "shiift", "shifft"]],

  // === MULTI-WORD PATTERNS (must come before their component single-word patterns) ===

  // Contractions - normalize to short form
  ["dont", ["do not"]],
  ["didnt", ["did not"]],
  ["shouldnt", ["should not"]],
  ["im", ["i am"]],
  ["ive", ["i have"]],
  ["ill", ["i will"]],
  ["its", ["it is"]],
  ["thats", ["that is"]],

  // SEMANTIC GROUP: Inability expressions → "cant"
  // All these express inability/unwillingness in a current or future tense
  ["cant", ["cannot", "can not", "wont", "will not", "unable to", "not able to"]],

  // SEMANTIC GROUP: Attendance verbs → "come"
  // All these mean "arriving/attending"
  ["come", ["attend", "show up", "be there", "make it"]],

  // Future/obligation
  ["going to", ["gonna", "gotta", "got to"]],

  // === SINGLE-WORD PATTERNS ===

  // Modal verbs - normalize to "can" (for polite requests like "could you cancel")
  // Must come after multi-word patterns containing "could"/"would"
  ["can", ["could", "would"]],

  // Politeness words → "please"
  ["please", ["kindly", "mind"]],
];

/**
 * Builds regex patterns from the word normalization mapping.
 * Preserves array order since WORD_NORMALIZATIONS is already ordered correctly
 */
function buildNormalizationPatterns(): Array<[RegExp, string]> {
  const patterns: Array<[RegExp, string]> = [];

  for (const [canonical, alternates] of WORD_NORMALIZATIONS) {
    const conjoinedAlternates = alternates.join("|");
    // eslint-disable-next-line security/detect-non-literal-regexp -- Pattern built from hardcoded WORD_NORMALIZATIONS constant, not user input
    const pattern = new RegExp(`\\b(?:${conjoinedAlternates})\\b`, "g");
    patterns.push([pattern, canonical]);
  }

  return patterns;
}

const NORMALIZATION_PATTERNS = buildNormalizationPatterns();

/**
 * Normalizes a message for pattern matching by:
 * 1. Converting to lowercase
 * 2. Filtering to only [a-z0-9,.?!\s] (removes apostrophes, special chars)
 * 3. Collapsing contiguous whitespace to single space
 * 4. Replacing typos and synonyms with canonical forms
 */
export function normalizeMessage(message: string): string {
  let normalized = message
    .toLowerCase()
    .replace(/[^a-z0-9,.?!\s]/g, "")
    .replace(/\s+/g, " ")
    .trim();

  for (const [pattern, replacement] of NORMALIZATION_PATTERNS) {
    normalized = normalized.replace(pattern, replacement);
  }

  return normalized;
}

/**
 * Patterns that will trigger an alert, if found.
 *
 * Patterns match against normalized messages, so they can be simpler:
 * - No apostrophe variants (can't → cant after normalization)
 * - No typo variants (cancle → cancel after normalization)
 * - Single space instead of \s+ (whitespace is normalized)
 * - No /i flag needed (messages are lowercased)
 */
const CANCELLATION_PATTERNS = [
  // "can you [please/mind/kindly] cancel" (could/would normalized to can)
  /can you (?:please ){0,3}(?:cancel|remove me|drop)/,
  // "like/want/need to cancel"
  /(?:like|want|need) (you )?to (?:cancel|remove me|drop)/,
  // "cancel my [adjective] shift"
  // eslint-disable-next-line security/detect-unsafe-regex -- The backtracking is tightly bounded.
  /(?:cancel|remove|drop) (?:my|the|this) (?:[a-z0-9]{1,30} )?shift/,
  // "cancel <word> shift for me"
  /cancel [a-z0-9]{1,30} shift for me/,
  // "take me off the schedule/shift"
  /take me off the (?:shift|schedule)/,
  // "gotta cancel" / "gonna have to cancel"
  /going to (?:have to )?(?:cancel|miss|bail)/,
  // "get/have my [adjective] shift canceled"
  // eslint-disable-next-line security/detect-unsafe-regex -- The backtracking is tightly bounded.
  /(?:get|have) (?:my|the|this) (?:[a-z0-9]{1,30} )?shift (?:canceled|dropped)/,
] as const;

/**
 * Patterns that should prevent an alert, if found, even if a pattern {@link CANCELLATION_PATTERNS} is found as well.
 */
const NEGATION_PATTERNS = [
  // Direct negation
  /(?:cant|dont|shouldnt|didnt|not) (?:(?:have|want|need) to )?cancel/,
  // Past tense
  /canceled/,
  /had to cancel/,
  // Informative
  /\b(?:im|ill) (?:going to )?(?:cancel|drop)/,
  /\bill go (?:ahead )?and (?:cancel|drop)/,
  // Questions
  /did (?:you|they|someone) cancel/,
  /are you going to cancel/,
  // Information seeking
  /how (?:do|can) i cancel/,
  /(?:fee|penalty|policy) (?:to cancel|for canceling)/,
  // Still coming despite issues (be there/make it → come)
  /im on my way/,
  /still (?:come|coming|heading)/,
  // Policy/timing questions (not requests) - note: "too" normalizes to "to"
  /(?:to late|still time) to cancel/,
] as const;

/**
 * Normalizes the message and checks if it contains a request to cancel a shift.
 *
 * Returns true if, after normalization, the given message...
 * 1. matches one of {@link CANCELLATION_PATTERNS}, and
 * 2. does NOT match any of {@link NEGATION_PATTERNS}
 */
export function detectCancellationRequest(message: string): boolean {
  const normalized = normalizeMessage(message);

  // Ignore empty messages
  if (!normalized) {
    return false;
  }

  // Ignore any message that matches one of the negations.
  if (NEGATION_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return false;
  }

  // Alert on messages that match cancellation pattern
  if (CANCELLATION_PATTERNS.some((pattern) => pattern.test(normalized))) {
    return true;
  }

  return false;
}

interface CancellationDetection {
  /**
   * Checks if a message contains a cancellation request.
   * Logs analytics event if a cancellation is detected.
   * Returns false if feature flag is disabled.
   */
  checkMessage: (message: string) => boolean;
  /**
   * Logs an analytics event for the user's action on the cancellation alert.
   */
  logAction: (action: CancellationAlertAction, message: string) => void;
}

/**
 * Returns functions to check for cancellation requests and log analytics.
 * If the feature flag is not enabled, checkMessage will always return false.
 */
export function useCancellationDetection(): CancellationDetection {
  const isFeatureEnabled = useCbhFlag(CbhFeatureFlag.CHAT_CANCELLATION_DETECTION, {
    defaultValue: false,
  });

  const checkMessage = useCallback(
    (message: string) => {
      if (!isFeatureEnabled) {
        return false;
      }

      const isCancellation = detectCancellationRequest(message);

      if (isCancellation) {
        logEvent(APP_V2_USER_EVENTS.CANCELLATION_ALERT_TRIGGERED, {
          message,
          messageLength: message.length,
        });
      }

      return isCancellation;
    },
    [isFeatureEnabled]
  );

  const logAction = useCallback((action: CancellationAlertAction, message: string) => {
    logEvent(APP_V2_USER_EVENTS.CANCELLATION_ALERT_ACTION, {
      action,
      message,
      messageLength: message.length,
    });
  }, []);

  return { checkMessage, logAction };
}
